import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  GMServiceVisitByDealer,
  GMServiceVisitByVin,
  GMServiceVisitDtc,
  GMServiceVisitDtcCount,
  GMServiceVisitVehicleData,
} from '@quorum/models/oem-resource';
import {
  SVC_VEHICLE_DIAGNOSTICS_ROOT_FEATURE_KEY,
  State,
  serviceVisitByVinAdapter,
  serviceVisitByDealerAdapter,
  linkedServiceVisitDtcAdapter,
  linkedServiceVisitMpviAdapter,
  linkedServiceVisitEcuAdapter,
} from './svc-vehicle-diagnostics-root.reducer';

// Lookup the 'SvcVehicleDiagnosticsRoot' feature state managed by NgRx
export const getSvcVehicleDiagnosticsRootState = createFeatureSelector<State>(SVC_VEHICLE_DIAGNOSTICS_ROOT_FEATURE_KEY);

const serviceVisitsByVinState = createSelector(
  getSvcVehicleDiagnosticsRootState,
  (state: State) => state.GM.serviceVisitsByVin
);

const serviceVisitsByVinSelectors = serviceVisitByVinAdapter.getSelectors(serviceVisitsByVinState);

export const selectServiceVisitsByVin = createSelector(
  serviceVisitsByVinSelectors.selectAll,
  (serviceVisitsByVin: GMServiceVisitByVin[]) => serviceVisitsByVin
);

const selectServiceVisitByVinStatus = createSelector(
  getSvcVehicleDiagnosticsRootState,
  (state: State) => state.GM.serviceVisitsByVin.status
);

const selectServiceVisitByVinError = createSelector(
  getSvcVehicleDiagnosticsRootState,
  (state: State) => state.GM.serviceVisitsByVin.error
);

const serviceVisitsByDealerState = createSelector(
  getSvcVehicleDiagnosticsRootState,
  (state: State) => state.GM.serviceVisitsByDealer
);

const serviceVisitsByDealerSelectors = serviceVisitByDealerAdapter.getSelectors(serviceVisitsByDealerState);

export const selectServiceVisitsByDealer = createSelector(
  serviceVisitsByDealerSelectors.selectAll,
  (serviceVisitsByDealer: GMServiceVisitByDealer[]) => serviceVisitsByDealer
);

const selectServiceVisitsByDealerStatus = createSelector(
  getSvcVehicleDiagnosticsRootState,
  (state: State) => state.GM.serviceVisitsByDealer.status
);

const selectServiceVisitsByDealerError = createSelector(
  getSvcVehicleDiagnosticsRootState,
  (state: State) => state.GM.serviceVisitsByDealer.error
);

const selectLinkedServiceVisitId = createSelector(
  getSvcVehicleDiagnosticsRootState,
  (state: State) => state.GM.linkedServiceVisitId
);

const selectLinkedServiceVisit = createSelector(
  serviceVisitsByVinSelectors.selectEntities,
  serviceVisitsByDealerSelectors.selectEntities,
  selectLinkedServiceVisitId,
  (serviceVisitByVinEntities, serviceVisitByDealerEntities, linkedVisitId) => {
    if (serviceVisitByVinEntities[linkedVisitId]) {
      return serviceVisitByVinEntities[linkedVisitId];
    } else if (serviceVisitByDealerEntities[linkedVisitId]) {
      return serviceVisitByDealerEntities[linkedVisitId];
    } else {
      return null;
    }
  }
);

const linkedServiceVisitDtcState = createSelector(
  getSvcVehicleDiagnosticsRootState,
  (state: State) => state.GM.linkedServiceVisitDtc
);

const linkedServiceVisitDtcSelectors = linkedServiceVisitDtcAdapter.getSelectors(linkedServiceVisitDtcState);

export const selectLinkedServiceVisitDtcs = createSelector(
  linkedServiceVisitDtcSelectors.selectAll,
  (linkedServiceVisitDtcs: GMServiceVisitDtc[]) => linkedServiceVisitDtcs
);

export const selectLinkedServiceVisitDtcsStatus = createSelector(linkedServiceVisitDtcState, (state) => state.status);

export const selectLinkedServiceVisitDtcsError = createSelector(linkedServiceVisitDtcState, (state) => state.error);

const linkedServiceVisitMpviState = createSelector(
  getSvcVehicleDiagnosticsRootState,
  (state: State) => state.GM.linkedServiceVisitMpvi
);

const linkedServiceVisitMpviSelectors = linkedServiceVisitMpviAdapter.getSelectors(linkedServiceVisitMpviState);

export const selectLinkedServiceVisitMpvi = createSelector(
  linkedServiceVisitMpviSelectors.selectAll,
  (linkedServiceVisitMpvi: GMServiceVisitVehicleData[]) => linkedServiceVisitMpvi
);

export const selectLinkedServiceVisitMpviStatus = createSelector(linkedServiceVisitMpviState, (state) => state.status);

export const selectLinkedServiceVisitMpviError = createSelector(linkedServiceVisitMpviState, (state) => state.error);

const linkedServiceVisitEcuState = createSelector(
  getSvcVehicleDiagnosticsRootState,
  (state: State) => state.GM.linkedServiceVisitEcu
);

const linkedServiceVisitEcuSelectors = linkedServiceVisitEcuAdapter.getSelectors(linkedServiceVisitEcuState);

export const selectLinkedServiceVisitEcus = createSelector(
  linkedServiceVisitEcuSelectors.selectAll,
  (linkedServiceVisitEcus: GMServiceVisitDtcCount[]) => linkedServiceVisitEcus
);

export const selectLinkedServiceVisitEcusStatus = createSelector(linkedServiceVisitEcuState, (state) => state.status);

export const selectLinkedServiceVisitEcusError = createSelector(linkedServiceVisitEcuState, (state) => state.error);

export const selectRecentServiceVisitBasedOnVin = createSelector(
  selectServiceVisitsByVin,
  selectServiceVisitsByDealer,
  (serviceVisitByVin, serviceVisitByDealer) => {
    if (serviceVisitByVin.length > 0) {
      if (serviceVisitByDealer.length > 0) {
        const combinedArray = serviceVisitByVin.concat(
          serviceVisitByDealer.filter((visitByDealer) => visitByDealer.vin === serviceVisitByVin[0].vin)
        );
        return combinedArray.sort((serviceVisitA: any, serviceVisitB: any) => {
          const checkInDateA = new Date(
            parseInt(serviceVisitA.checkInDate.split('-')[0]),
            parseInt(serviceVisitA.checkInDate.split('-')[1]) - 1,
            parseInt(serviceVisitA.checkInDate.split('-')[2]),
            parseInt(serviceVisitA.checkInTime.split(':')[0]),
            parseInt(serviceVisitA.checkInTime.split(':')[1]),
            parseInt(serviceVisitA.checkInTime.split(':')[2]),
            parseInt(serviceVisitA.checkInTime.split('.')[1])
          );
          const checkInDateB = new Date(
            parseInt(serviceVisitB.checkInDate.split('-')[0]),
            parseInt(serviceVisitB.checkInDate.split('-')[1]) - 1,
            parseInt(serviceVisitB.checkInDate.split('-')[2]),
            parseInt(serviceVisitB.checkInTime.split(':')[0]),
            parseInt(serviceVisitB.checkInTime.split(':')[1]),
            parseInt(serviceVisitB.checkInTime.split(':')[2]),
            parseInt(serviceVisitB.checkInTime.split('.')[1])
          );
          return checkInDateB.getTime() - checkInDateA.getTime();
        })[0];
      } else {
        return serviceVisitByVin.sort((serviceVisitA: any, serviceVisitB: any) => {
          const checkInDateA = new Date(
            parseInt(serviceVisitA.checkInDate.split('-')[0]),
            parseInt(serviceVisitA.checkInDate.split('-')[1]) - 1,
            parseInt(serviceVisitA.checkInDate.split('-')[2]),
            parseInt(serviceVisitA.checkInTime.split(':')[0]),
            parseInt(serviceVisitA.checkInTime.split(':')[1]),
            parseInt(serviceVisitA.checkInTime.split(':')[2]),
            parseInt(serviceVisitA.checkInTime.split('.')[1])
          );
          const checkInDateB = new Date(
            parseInt(serviceVisitB.checkInDate.split('-')[0]),
            parseInt(serviceVisitB.checkInDate.split('-')[1]) - 1,
            parseInt(serviceVisitB.checkInDate.split('-')[2]),
            parseInt(serviceVisitB.checkInTime.split(':')[0]),
            parseInt(serviceVisitB.checkInTime.split(':')[1]),
            parseInt(serviceVisitB.checkInTime.split(':')[2]),
            parseInt(serviceVisitB.checkInTime.split('.')[1])
          );
          return checkInDateB.getTime() - checkInDateA.getTime();
        })[0];
      }
    } else {
      return null;
    }
  }
);

export const vehicleDiagnosticsQuery = {
  selectServiceVisitsByVin,
  selectServiceVisitsByDealer,
  selectLinkedServiceVisitId,
  selectLinkedServiceVisit,
  selectLinkedServiceVisitDtcs,
  selectLinkedServiceVisitMpvi,
  selectLinkedServiceVisitEcus,
  selectServiceVisitByVinStatus,
  selectServiceVisitsByDealerStatus,
  selectLinkedServiceVisitDtcsStatus,
  selectLinkedServiceVisitMpviStatus,
  selectLinkedServiceVisitEcusStatus,
  selectRecentServiceVisitBasedOnVin,
  selectServiceVisitByVinError,
  selectServiceVisitsByDealerError,
  selectLinkedServiceVisitDtcsError,
  selectLinkedServiceVisitEcusError,
  selectLinkedServiceVisitMpviError,
};
